import React, { useState, useEffect } from 'react';
import { MapPin, ChevronRight, Calendar, Users, Loader2, Search, Filter, AlertCircle, Projector, FileText, X, Globe, Check, Eye, TrendingUp, Clock, Award, Building } from 'lucide-react';
import Login from '../Pages/Login';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../StoreContext/LanguageContext'; // Import language hook
import { useLocation } from 'react-router-dom';
import apiService from '../config'; // Import apiService from config

// Statuses for filtering
const statuses = [
  "All Status",
  "Active Projects",
  "Inactive Projects",
  "Preliminary survey",
  "Notification issued",
  "Land survey completed",
  "Land marking in progress",
  "Site inspection completed",
  "Compensation processing",
  "Compensation finalized",
  "Final assessment"
];

export default function LandAcquisitionProjects() {
  const { language, changeLanguage, translations } = useLanguage();
  const [showLanguage, setShowLanguage] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [showLoginPopup, setShowLoginPopup] = useState(false);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("All Status");
  const [currentPage, setCurrentPage] = useState(1);
  const [activeProject, setActiveProject] = useState(null);
  const [projectDetailsLoading, setProjectDetailsLoading] = useState(false);
  const [viewType, setViewType] = useState("grid");
  const [hoveredCard, setHoveredCard] = useState(null);
  const [searchFocused, setSearchFocused] = useState(false);
  const [proposals, setProposals] = useState([]);
  const [selectedProposalId, setSelectedProposalId] = useState('');
  const [proposalError, setProposalError] = useState('');




  const languageCodeMap = {
    'EN': 'en',
    'HI': 'hi',
    'MR': 'mr',
    'ES': 'es'
  };

  const projectsPerPage = 8;

  // Function to clear all filters
  const clearFilters = () => {
    setSearchTerm("");
    setFilterStatus("All Status");
    setCurrentPage(1);
  };


  const handleRaiseObjection = (e, projectId = null) => {
    // Validate proposal selection first
    if (!selectedProposalId) {
      setProposalError('Proposal selection is required.');
      return;
    }

    if (e) {
      e.stopPropagation();
    }

    let selectedProject = null;
    let selectedProposal = null;

    if (projectId) {
      // Find the project
      selectedProject = projects.find(project =>
        project.id === projectId ||
        project.project_id === projectId ||
        project.value === projectId
      );
    } else if (activeProject) {
      selectedProject = activeProject;
    }

    // Find the selected proposal from the proposals array
    selectedProposal = proposals.find(proposal =>
      String(proposal.acquisition_proposal_id) === selectedProposalId
    );

    if (!selectedProposal) {
      setProposalError('Selected proposal not found. Please select a valid proposal.');
      return;
    }


    // Validate that the proposal allows objections
    const hasGeneralObjection = selectedProposal.general_objection === true;
    const hasPrivateObjection = selectedProposal.private_objection === true;

    // Updated logic: if both are true, proceed to PrivateObjection
    // Only prevent if both are false
    if (!hasGeneralObjection && !hasPrivateObjection) {
      setProposalError(`Proposal ${selectedProposal.acquisition_proposal_no || selectedProposal.acquisition_proposal_id} does not allow any objections.`);
      return;
    }

    console.log('Selected Proposal with objection flags:', {
      proposalId: selectedProposal.acquisition_proposal_id,
      proposalNo: selectedProposal.acquisition_proposal_no,
      generalObjection: hasGeneralObjection,
      privateObjection: hasPrivateObjection
    });

    if (selectedProject) {
      // Handle multiple possible field names for project
      const id = selectedProject.project_id || selectedProject.id || selectedProject.value;
      const name = selectedProject.project_name || selectedProject.title || selectedProject.label;

      console.log('Setting project:', { id, name });

      // Store project details in sessionStorage
      sessionStorage.setItem('selectedProjectId', id);
      sessionStorage.setItem('selectedProjectName', name);
      sessionStorage.setItem('selectedProjectData', JSON.stringify(selectedProject));

      // Store proposal details in sessionStorage (MOST IMPORTANT FOR OBJECTION ROUTING)
      sessionStorage.setItem('selectedProposalId', selectedProposal.acquisition_proposal_id);
      sessionStorage.setItem('selectedProposalNo', selectedProposal.acquisition_proposal_no || '');
      sessionStorage.setItem('selectedProposalData', JSON.stringify(selectedProposal));
    } else {
      console.log('No project found for ID:', projectId);
    }

    // Always show login popup first
    setShowLoginPopup(true);
  };

  const toggleLoginPopup = () => {
    setShowLoginPopup(!showLoginPopup);
  };

  const handleCloseLogin = () => {
    setShowLoginPopup(false);
  };

  // Fetch all projects from API (initial load without projectId)
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setLoading(true);
        // Get all projects without projectId parameter
        const result = await apiService.getAllProjects();

        // Handle both single project and array of projects
        let projectsData = [];
        if (result.status === 200) {
          if (Array.isArray(result.data)) {
            projectsData = result.data;
          } else {
            projectsData = [result.data];
          }
        }

        // Transform API data to match component structure
        const transformedProjects = projectsData.map(project => ({
          id: project.project_id,
          title: project.project_name,
          location: `${project.eng_village_name || ''}, ${project.eng_tehsil_name || ''}`.replace(/^,\s*|,\s*$/g, '') || 'Location not specified',
          area: `${project.no_of_budharak || 0} bhudharak`,
          // status: getStatusFromProject(project),
          status: project.project_division,
          // Use API percentage first, then fallback to calculated progress
          progress: project.percentage !== undefined && project.percentage !== null
            ? parseInt(project.percentage) || 0
            : getProgressFromProject(project),
          date: formatDate(project.last_upd_dt),
          stakeholders: project.no_of_budharak || 0,
          image: project.project_banner || "/api/placeholder/400/300",
          description: `${project.project_type || 'Development'} project in ${project.project_division || 'Buldhana'} division. Land acquisition number: ${project.land_acquisition_no || 'N/A'}`,
          division: project.project_division,
          type: project.project_type,
          landAcquisitionNo: project.land_acquisition_no,
          district: project.land_district,
          adminOrderNo: project.administrative_order_no,
          adminOrderDate: project.administrative_order_date,
          proposalNo: project.acquisition_proposal_id,
          proposalDate: project.acquisition_proposal_date,
          inchargeId: project.project_incharge_usr_id,
          isActive: project.isactive,
          createdDate: project.cre_usr_dt,
          updatedDate: project.last_upd_dt,
          // Store the raw API percentage for reference
          apiPercentage: project.percentage,

          general_objection: project.general_objection,
          private_objection: project.private_objection,
          generalObjection: project.generalObjection,
          privateObjection: project.privateObjection
        }));

        setProjects(transformedProjects);
        setError(null);
      } catch (err) {
        setError(`Failed to fetch projects: ${err.message}`);
        console.error('Error fetching projects:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  // Handle navigation from homepage - Auto-open project details if selectedProject is passed
  useEffect(() => {
    if (location.state?.selectedProject && projects.length > 0) {
      // Find the project with matching ID
      const selectedProject = projects.find(project => project.id === location.state.selectedProject);

      if (selectedProject) {
        // Set search term if provided
        if (location.state.searchTerm) {
          setSearchTerm(location.state.searchTerm);
        }

        // Auto-open the project details modal
        handleProjectClick(selectedProject);
      }

      // Clear the navigation state to prevent reopening on refresh
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [projects, location.state]);

  // Function to handle project click and fetch detailed project data
  const handleProjectClick = async (project) => {
    try {
      setProjectDetailsLoading(true);

      // Fetch detailed project data using projectId
      const result = await apiService.getAllProjects(project.id);
      const proposalResponse = await apiService.getProposalsByProjectId(project.id);
      if (proposalResponse.status === 200) {
        setProposals(proposalResponse.data);
      }

      let detailedProjectData = null;
      if (result.status === 200) {
        if (Array.isArray(result.data)) {
          detailedProjectData = result.data[0];
        } else {
          detailedProjectData = result.data;
        }
      }

      if (detailedProjectData) {
        // Transform detailed project data
        const detailedProject = {
          id: detailedProjectData.project_id,
          title: detailedProjectData.project_name,
          location: `${detailedProjectData.eng_village_name || ''}, ${detailedProjectData.eng_tehsil_name || ''}`.replace(/^,\s*|,\s*$/g, '') || 'Location not specified',
          area: `${detailedProjectData.no_of_budharak || 0} bhudharak`,
          status: detailedProjectData.project_division,
          // Use API percentage first, then fallback to calculated progress
          progress: detailedProjectData.percentage !== undefined && detailedProjectData.percentage !== null
            ? parseInt(detailedProjectData.percentage) || 0
            : getProgressFromProject(detailedProjectData),
          date: formatDate(detailedProjectData.last_upd_dt),
          stakeholders: detailedProjectData.no_of_budharak || 0,
          image: detailedProjectData.project_banner || "/api/placeholder/400/300",
          description: `${detailedProjectData.project_type || 'Development'} project in ${detailedProjectData.project_division || 'Buldhana'} division. Land acquisition number: ${detailedProjectData.land_acquisition_no || 'N/A'}`,
          division: detailedProjectData.project_division,
          type: detailedProjectData.project_type,
          landAcquisitionNo: detailedProjectData.land_acquisition_no,
          district: detailedProjectData.land_district,
          adminOrderNo: detailedProjectData.administrative_order_no,
          adminOrderDate: detailedProjectData.administrative_order_date,
          proposalNo: detailedProjectData.acquisition_proposal_id,
          proposalDate: detailedProjectData.acquisition_proposal_date,
          inchargeId: detailedProjectData.project_incharge_usr_id,
          isActive: detailedProjectData.isactive,
          createdDate: detailedProjectData.cre_usr_dt,
          updatedDate: detailedProjectData.last_upd_dt,
          // Store the raw API percentage for reference
          apiPercentage: detailedProjectData.percentage
        };

        setActiveProject(detailedProject);
      } else {
        // Fallback to original project data if detailed fetch fails
        setActiveProject(project);
      }
    } catch (err) {
      console.error('Error fetching project details:', err);
      // Fallback to original project data if detailed fetch fails
      setActiveProject(project);
    } finally {
      setProjectDetailsLoading(false);
    }
  };

  // Helper function to determine status from project data
  // const getStatusFromProject = (project) => {
  //   if (project.administrative_order_date && project.acquisition_proposal_date) {
  //     return "Final assessment";
  //   } else if (project.administrative_order_no) {
  //     return "Compensation processing";
  //   } else if (project.acquisition_proposal_no) {
  //     return "Land survey completed";
  //   } else if (project.land_acquisition_no) {
  //     return "Notification issued";
  //   } else {
  //     return "Preliminary survey";
  //   }
  // };

  // Helper function to calculate progress based on project data (fallback method)
  const getProgressFromProject = (project) => {
    // If API provides percentage, use it
    if (project.percentage !== undefined && project.percentage !== null) {
      const apiProgress = parseInt(project.percentage);
      return isNaN(apiProgress) ? 0 : Math.max(0, Math.min(100, apiProgress));
    }

    // Fallback calculation if percentage is not provided by API
    let progress = 10; // Base progress

    if (project.land_acquisition_no) progress += 20;
    if (project.acquisition_proposal_no) progress += 25;
    if (project.administrative_order_no) progress += 25;
    if (project.administrative_order_date) progress += 15;
    if (project.acquisition_proposal_date) progress += 5;

    return Math.min(progress, 100);
  };

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return "Date not set";

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return "Invalid date";
    }
  };

  // Filter projects based on search term and status
  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      project.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (project.type && project.type.toLowerCase().includes(searchTerm.toLowerCase()));

    let matchesStatus = true;
    if (filterStatus === "Active Projects") {
      matchesStatus = project.isActive === true;
    } else if (filterStatus === "Inactive Projects") {
      matchesStatus = project.isActive === false;
    } else if (filterStatus !== "All Status") {
      matchesStatus = project.status === filterStatus;
    }

    return matchesSearch && matchesStatus;
  });

  // Calculate pagination
  const indexOfLastProject = currentPage * projectsPerPage;
  const indexOfFirstProject = indexOfLastProject - projectsPerPage;
  const currentProjects = filteredProjects.slice(indexOfFirstProject, indexOfLastProject);
  const totalPages = Math.ceil(filteredProjects.length / projectsPerPage);

  // Enhanced Project Cards with better design
  const ProjectCard = ({ project, index }) => (
    <div
      className={`group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer overflow-hidden border border-gray-100 transform hover:-translate-y-2 ${hoveredCard === project.id ? 'scale-105' : ''
        }`}
      onClick={() => handleProjectClick(project)}
      onMouseEnter={() => setHoveredCard(project.id)}
      onMouseLeave={() => setHoveredCard(null)}
      style={{
        animationDelay: `${index * 100}ms`,
        animation: `fadeInUp 0.6s ease-out forwards`
      }}
    >
      {/* Enhanced Image Section */}
      <div className="relative h-56 overflow-hidden">
        {project.image && project.image !== '' && !project.image.includes('undefined') && !project.image.includes('/api/placeholder') ? (
          <>
            <img
              src={project.image}
              alt={project.title}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              onError={(e) => {
                e.target.style.display = 'none';
                const noImageDiv = e.target.nextElementSibling;
                if (noImageDiv) {
                  noImageDiv.style.display = 'flex';
                }
              }}
            />

            <div
              className="absolute inset-0 bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center"
              style={{ display: 'none' }}
            >
              <div className="text-center text-gray-600">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-16 w-16 mx-auto mb-3 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <p className="text-sm font-medium">No Image</p>
              </div>
            </div>
          </>
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
            <div className="text-center text-gray-600">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-16 w-16 mx-auto mb-3 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
              <p className="text-sm font-medium">No Image</p>
            </div>
          </div>
        )}

        {project.image && project.image !== '' && !project.image.includes('undefined') && !project.image.includes('/api/placeholder') && (
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        )}

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

        {/* Status Badge */}
        <div className="absolute top-4 right-4">
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-bold backdrop-blur-md ${project.isActive
            ? 'bg-green-500/90 text-white shadow-green-500/25'
            : 'bg-gray-500/90 text-white shadow-gray-500/25'
            } shadow-lg`}>
            <div className={`w-2 h-2 rounded-full mr-2 ${project.isActive ? 'bg-green-300' : 'bg-gray-300'
              }`} />
            {project.isActive ? 'Active' : 'Inactive'}
          </span>
        </div>

        {/* Progress Badge */}
        <div className="absolute top-4 left-4">
          <div className="flex items-center px-3 py-1 rounded-full bg-yellow-500/90 backdrop-blur-md text-white text-xs font-bold shadow-lg">
            <TrendingUp size={12} className="mr-1" />
            {project.progress}%
          </div>
        </div>

        {/* Title Overlay */}
        <div className="absolute bottom-4 left-4 right-4">
          <h3 className="text-white font-bold text-lg leading-tight mb-1 drop-shadow-lg">
            {project.title}
          </h3>
          <div className="flex items-center text-white/90 text-sm">
            <MapPin size={14} className="mr-1 text-yellow-300" />
            <span className="truncate">{project.location}</span>
          </div>
        </div>
      </div>

      {/* Enhanced Content Section */}
      <div className="p-6">
        {/* Status and Type */}
        <div className="flex items-center justify-between mb-4">
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200">
            <Award size={10} className="mr-1" />
            {project.status}
          </span>
          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
            {project.type || 'Development'}
          </span>
        </div>

        {/* Enhanced Progress Bar */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">{translations[language].Progress}</span>
            <span className="text-sm font-bold text-yellow-600">{project.progress}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2.5 shadow-inner">
            <div
              className="bg-gradient-to-r from-yellow-400 to-yellow-500 h-2.5 rounded-full transition-all duration-500 shadow-sm relative overflow-hidden"
              style={{ width: `${project.progress}%` }}
            >
              <div className="absolute inset-0 bg-white/20 animate-pulse" />
            </div>
          </div>
        </div>

        {/* Project Stats */}
        {/* <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-100">
            <div className="flex items-center justify-center mb-1">
              <Building size={16} className="text-blue-600" />
            </div>
            <div className="text-lg font-bold text-blue-800">{project.area}</div>
            <div className="text-xs text-blue-600">Area</div>
          </div>
          <div className="text-center p-3 bg-green-50 rounded-lg border border-green-100">
            <div className="flex items-center justify-center mb-1">
              <Users size={16} className="text-green-600" />
            </div>
            <div className="text-lg font-bold text-green-800">{project.stakeholders}</div>
            <div className="text-xs text-green-600">Stakeholders</div>
          </div>
        </div> */}

        {/* Date and Action */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-100">
          <div className="flex items-center text-gray-500 text-sm">
            <Clock size={14} className="mr-2 text-yellow-500" />
            <span>{project.date}</span>
          </div>
          <div className="flex items-center text-yellow-600 text-sm font-medium group-hover:text-yellow-700 transition-colors">
            <Eye size={14} className="mr-1" />
            {translations[language].ViewDetails}
            <ChevronRight size={14} className="ml-1 transform group-hover:translate-x-1 transition-transform" />
          </div>
        </div>
      </div>

      {/* Hover Effect Overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-400/10 to-blue-600/10 opacity-10 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
    </div>
  );

  // Enhanced Project detail modal
  const ProjectDetail = ({ project, onClose, loading }) => {
    if (!project) return null;

    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-300">
        <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[90vh] overflow-y-auto border border-gray-200 animate-in slide-in-from-bottom-4 duration-500">
          {loading ? (
            <div className="flex items-center justify-center p-12">
              <div className="text-center">
                <Loader2 size={48} className="animate-spin text-yellow-500 mx-auto mb-4" />
                <span className="text-lg font-medium text-gray-700">Loading project</span>
                <p className="text-gray-500 mt-2">Please wait</p>
              </div>
            </div>
          ) : (
            <>
              {/* Enhanced Header Image */}
              <div className="relative h-80 overflow-hidden rounded-t-2xl">
                {project.image && project.image !== '' && !project.image.includes('undefined') && !project.image.includes('/api/placeholder') ? (
                  <>
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        const noImageDiv = e.target.nextElementSibling;
                        if (noImageDiv) {
                          noImageDiv.style.display = 'flex';
                        }
                      }}
                    />

                    <div
                      className="absolute inset-0 bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center"
                      style={{ display: 'none' }}
                    >
                      <div className="text-center text-gray-600">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-24 w-24 mx-auto mb-4 text-gray-400"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={1}
                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                        <p className="text-lg font-medium">No Image Available</p>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="absolute inset-0 bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                    <div className="text-center text-gray-600">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-24 w-24 mx-auto mb-4 text-gray-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={1}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      <p className="text-lg font-medium">No Image Available</p>
                    </div>
                  </div>
                )}

                {project.image && project.image !== '' && !project.image.includes('undefined') && !project.image.includes('/api/placeholder') && (
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30" />
                )}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/30" />

                {/* Close Button */}
                <button
                  onClick={onClose}
                  className="absolute top-6 right-6 bg-white/90 backdrop-blur-sm rounded-full p-2 shadow-lg hover:bg-white hover:scale-110 transition-all duration-200"
                >
                  <X size={20} className="text-gray-700" />
                </button>

                {/* Title Overlay */}
                <div className="absolute bottom-6 left-6 right-6">
                  <h2 className="text-3xl font-bold text-white mb-2 drop-shadow-lg">{project.title}</h2>
                  <div className="flex items-center text-white/90 text-lg">
                    <MapPin size={20} className="mr-2 text-yellow-300" />
                    <span>{project.location}</span>
                  </div>
                </div>
              </div>

              <div className="p-8">
                {/* Enhanced Stats Grid */}
                {/* <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl border border-blue-200">
                    <div className="flex items-center justify-between mb-3">
                      <Building className="text-blue-600" size={24} />
                      <span className="text-xs font-medium text-blue-600 bg-blue-200 px-2 py-1 rounded-full">{translations[language].Area}</span>
                    </div>
                    <h3 className="font-bold text-blue-900 text-lg">{translations[language].ProjectArea}</h3>
                    <p className="text-2xl font-bold text-blue-800">{project.area}</p>
                  </div>

                  <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-xl border border-green-200">
                    <div className="flex items-center justify-between mb-3">
                      <Award className="text-green-600" size={24} />
                      <span className="text-xs font-medium text-green-600 bg-green-200 px-2 py-1 rounded-full">{translations[language].Status}</span>
                    </div>
                    <h3 className="font-bold text-green-900 text-lg">{translations[language].status}</h3>
                    <p className="text-lg font-bold text-green-800">{project.status}</p>
                  </div>

                  <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 p-6 rounded-xl border border-yellow-200">
                    <div className="flex items-center justify-between mb-3">
                      <TrendingUp className="text-yellow-600" size={24} />
                      <span className="text-xs font-medium text-yellow-600 bg-yellow-200 px-2 py-1 rounded-full">{translations[language].Progress}</span>
                    </div>
                    <h3 className="font-bold text-yellow-900 text-lg">{translations[language].Progress}</h3>
                    <p className="text-2xl font-bold text-yellow-800">{project.progress}%</p>
                  </div>

                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-xl border border-purple-200">
                    <div className="flex items-center justify-between mb-3">
                      <Users className="text-purple-600" size={24} />
                      <span className="text-xs font-medium text-purple-600 bg-purple-200 px-2 py-1 rounded-full">{translations[language].People}</span>
                    </div>
                    <h3 className="font-bold text-purple-900 text-lg">{translations[language].BudharakCount}</h3>
                    <p className="text-2xl font-bold text-purple-800">{project.stakeholders}</p>
                  </div>
                </div> */}

                {/* Enhanced Progress Section */}
                <div className="mb-8 p-6 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl border border-yellow-200">
                  <h3 className="font-bold text-gray-800 text-xl mb-4 flex items-center">
                    <TrendingUp className="mr-2 text-yellow-600" size={24} />
                    {translations[language].ProgressOverview}
                  </h3>
                  <div className="w-full bg-gray-200 rounded-full h-6 shadow-inner">
                    <div
                      className="bg-gradient-to-r from-yellow-400 via-yellow-500 to-orange-500 h-6 rounded-full transition-all duration-1000 relative overflow-hidden shadow-lg"
                      style={{ width: `${project.progress}%` }}
                    >
                      <div className="absolute inset-0 bg-white/30 animate-pulse" />
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-slide" />
                    </div>
                  </div>
                  <div className="flex justify-between text-sm text-gray-600 mt-3">
                    <span>{translations[language].Started}</span>
                    <span className="font-bold text-yellow-700">{project.progress}% {translations[language].Completed}</span>
                    <span>{translations[language].Complete}</span>
                  </div>
                </div>

                {/* Enhanced Details Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                  <div className="space-y-4">
                    <h3 className="font-bold text-gray-800 text-xl border-b-2 border-yellow-500 pb-2">
                      {translations[language].ProjectInformation}
                    </h3>

                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                      <span className="font-semibold text-gray-700 block mb-1">{translations[language].ProjectType}</span>
                      <p className="text-gray-800">{project.type || 'Not specified'}</p>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                      <span className="font-semibold text-gray-700 block mb-1">{translations[language].Division}</span>
                      <p className="text-gray-800">{project.division || 'Not specified'}</p>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                      <span className="font-semibold text-gray-700 block mb-1">{translations[language].LastUpdated}</span>
                      <div className="flex items-center">
                        <Calendar size={16} className="mr-2 text-blue-500" />
                        <p className="text-gray-800">{project.date}</p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h3 className="font-bold text-gray-800 text-xl border-b-2 border-yellow-500 pb-2">
                      {translations[language].AdministrativeDetails}
                    </h3>

                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                      <span className="font-semibold text-gray-700 block mb-1">{translations[language].LandAcquisitionNo}</span>
                      <p className="text-gray-800">{project.landAcquisitionNo || 'Not assigned'}</p>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                      <span className="font-semibold text-gray-700 block mb-1">{translations[language].AdministrativeOrderNo}</span>
                      <p className="text-gray-800">{project.adminOrderNo || 'Not issued'}</p>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                      <span className="font-semibold text-gray-700 block mb-1">{translations[language].ProjectID}</span>
                      <p className="text-gray-800 font-mono">{project.id}</p>
                    </div>
                  </div>
                </div>
                <div className="mb-4 w-full md:w-1/2">
                  <label htmlFor="proposalSelect" className="block font-medium text-gray-700 mb-1">
                    {translations[language].SelectProposal} <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="proposalSelect"
                    className={`w-full border px-4 py-2 rounded-lg ${proposalError ? 'border-red-500' : 'border-gray-300'}`}
                    value={selectedProposalId}
                    onChange={(e) => {
                      const selectedId = e.target.value;
                      setSelectedProposalId(selectedId);
                      setProposalError('');

                      const selectedProposal = proposals.find(
                        (p) => String(p.acquisition_proposal_id) === selectedId
                      );

                      if (selectedProposal) {
                        console.log("Selected Proposal Details:", {
                          proposalId: selectedProposal.acquisition_proposal_id,
                          proposalNo: selectedProposal.acquisition_proposal_no,
                          generalObjection: selectedProposal.general_objection,
                          privateObjection: selectedProposal.private_objection
                        });

                        // Store both ID and NO in sessionStorage for immediate access
                        sessionStorage.setItem('selectedProposalId', selectedProposal.acquisition_proposal_id);
                        sessionStorage.setItem('selectedProposalNo', selectedProposal.acquisition_proposal_no || '');

                        // Updated feedback logic: both true = private objection available
                        const hasGeneral = selectedProposal.general_objection === true;
                        const hasPrivate = selectedProposal.private_objection === true;

                        if (hasGeneral && hasPrivate) {
                          // Both true - will navigate to Private Objection
                          setProposalError('');
                          console.log('Both objections available - will navigate to Private Objection');
                        } else if (!hasGeneral && !hasPrivate) {
                          // Both false - no objections available
                          setProposalError('This proposal does not allow any objections.');
                        } else {
                          // Only one is available
                          setProposalError('');
                          const availableType = hasGeneral ? 'General' : 'Private';
                          console.log(`Available objection type: ${availableType}`);
                        }
                      }
                    }}
                  >
                    <option value="">-- {translations[language].SelectProposal} --</option>
                    {proposals.map((proposal) => (
                      <option key={proposal.proposal_id} value={proposal.acquisition_proposal_id}>
                        {proposal.acquisition_proposal_no || `Proposal ${proposal.acquisition_proposal_no}`}
                      </option>
                    ))}
                  </select>
                  {proposalError && <p className="text-red-500 text-sm mt-1">{proposalError}</p>}
                </div>

                {/* Updated objection availability display */}
                {selectedProposalId && (
                  <div className="mt-2 text-sm">
                    {(() => {
                      const selected = proposals.find(p => String(p.acquisition_proposal_id) === selectedProposalId);
                      if (!selected) return null;

                      const hasGeneral = selected.general_objection === true;
                      const hasPrivate = selected.private_objection === true;

                      if (hasGeneral && hasPrivate) {
                        // Both true - will navigate to Private Objection
                        return (
                          <div className="p-2 rounded-md bg-blue-50 border border-blue-200">
                            <p className="text-blue-700 font-medium">
                              ✓ Available: Private Objection (both objection types are enabled)
                            </p>
                          </div>
                        );
                      } else if (!hasGeneral && !hasPrivate) {
                        // Both false - no objections
                        return (
                          <div className="p-2 rounded-md bg-red-50 border border-red-200">
                            <p className="text-red-700 font-medium">
                              ✗ No objections available for this proposal
                            </p>
                          </div>
                        );
                      } else {
                        // Only one is available
                        const available = [];
                        if (hasGeneral) available.push('General Objection');
                        if (hasPrivate) available.push('Private Objection');

                        return (
                          <div className="p-2 rounded-md bg-green-50 border border-green-200">
                            <p className="text-green-700 font-medium">
                              ✓ Available: {available.join(', ')}
                            </p>
                          </div>
                        );
                      }
                    })()}
                  </div>
                )}

                {/* Enhanced Description */}
                <div className="mb-8 p-6 bg-white border border-gray-200 rounded-xl shadow-sm">
                  <h3 className="font-bold text-gray-800 text-xl mb-4 flex items-center">
                    <FileText className="mr-2 text-yellow-600" size={24} />
                    {translations[language].Description1}
                  </h3>
                  <p className="text-gray-700 leading-relaxed">{project.description}</p>
                </div>

                {/* Enhanced Action Buttons */}
                <div className="flex flex-col sm:flex-row justify-between space-y-3 sm:space-y-0 sm:space-x-4 pt-6 border-t border-gray-200">
                  <button
                    onClick={onClose}
                    className="flex items-center justify-center px-6 py-3 bg-gray-500 hover:bg-gray-600 text-white rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                  >
                    <X size={18} className="mr-2" />
                    {translations[language].Close}
                  </button>

                  <button
                    onClick={(e) => {
                      // First validate proposal selection
                      if (!selectedProposalId) {
                        setProposalError('Please select a proposal before raising an objection.');
                        return;
                      }

                      // Check if selected proposal allows objections
                      const selectedProposal = proposals.find(
                        p => String(p.acquisition_proposal_id) === selectedProposalId
                      );

                      if (selectedProposal && !selectedProposal.general_objection && !selectedProposal.private_objection) {
                        setProposalError('The selected proposal does not allow objections.');
                        return;
                      }

                      // Proceed with objection process
                      handleRaiseObjection(e, project.id);
                    }}
                    className="flex items-center justify-center px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                  >
                    <FileText size={18} className="mr-2" />
                    {translations[language].RaiseObjection}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  // Loading state with enhanced design
  const handleLanguageChange = (code) => {
    const contextLangCode = languageCodeMap[code] || 'en';
    changeLanguage(contextLangCode);
    setShowLanguage(false);
  };

  // Get the current language code for display
  const getCurrentLanguageCode = () => {
    // Convert context language to UI language code
    for (const [uiCode, contextCode] of Object.entries(languageCodeMap)) {
      if (contextCode === language) {
        return uiCode;
      }
    }
    return 'EN'; // Default
  };

  const languageOptions = [
    { code: 'EN', name: 'English', flag: '🇺🇸' },
    { code: 'HI', name: 'हिंदी', flag: 'hi' },
    { code: 'MR', name: 'मराठी', flag: 'mr' },
    // { code: 'ES', name: 'Español', flag: '🇪🇸' }
  ];

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-yellow-50 via-orange-50 to-red-50 min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md mx-auto">
          <div className="relative">
            <div className="w-20 h-20 mx-auto mb-6">
              <div className="absolute inset-0 bg-yellow-500 rounded-full animate-ping opacity-20"></div>
              <div className="absolute inset-2 bg-yellow-400 rounded-full animate-pulse"></div>
              <Loader2 size={48} className="absolute inset-3 animate-spin text-yellow-600" />
            </div>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">{translations[language].LoadingProjects}...</h3>
          <p className="text-gray-600 mb-4">{translations[language].PleaseWait}.</p>
          <div className="w-64 h-2 bg-gray-200 rounded-full mx-auto overflow-hidden">
            <div className="h-full bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full animate-pulse"></div>
          </div>
        </div>
      </div>
    );
  }

  // Enhanced Error state
  if (error) {
    return (
      <div className="bg-gradient-to-br from-red-50 via-orange-50 to-yellow-50 min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-8 bg-white rounded-2xl shadow-2xl border border-red-100">
          <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
            <AlertCircle size={32} className="text-red-500" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Failed to Load Projects</h3>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white px-6 py-3 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gradient-to-br from-gray-50 via-white to-yellow-50 min-h-screen">
      {/* Enhanced Header */}
      {/* Compact Header */}
      <div className="bg-white shadow-md border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-gray-900 flex items-center">
                <div className="p-2 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-lg mr-3 shadow-md">
                  <Projector className="text-white" size={20} />
                </div>
                <div>
                  {translations[language].DevelopmentProjects}
                  <div className="text-xs font-normal text-gray-500 mt-0.5">
                    {translations[language].CurrentlyManaging} {projects.length} {translations[language].ProjectsInDistrict}
                  </div>
                </div>
              </h1>
            </div>

            <div className="flex items-center space-x-3">
              {/* Compact Language Switcher */}
              <div className="relative">
                <button
                  onClick={() => setShowLanguage(!showLanguage)}
                  className="relative p-2 rounded-lg hover:bg-blue-50 focus:outline-none transition-all duration-200 group bg-white shadow-sm border border-gray-200"
                  aria-label="Switch Language"
                >
                  <Globe className="h-4 w-4 text-gray-600 group-hover:text-blue-600 group-hover:scale-110 transition-all duration-200" />
                  <span className="absolute -bottom-0.5 -right-0.5 h-4 w-4 bg-blue-500 border-2 border-white rounded-full flex items-center justify-center text-[8px] text-white font-bold shadow-sm">
                    {getCurrentLanguageCode()}
                  </span>
                </button>

                {showLanguage && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl py-1 z-10 border border-gray-200 animate-in slide-in-from-top-2 duration-300">
                    <div className="px-3 py-2 border-b border-gray-200">
                      <h3 className="font-medium text-gray-800 flex items-center text-sm">
                        <Globe className="h-3 w-3 mr-2 text-blue-500" />
                        {translations[language]?.language || 'Language'}
                      </h3>
                    </div>
                    <div className="max-h-48 overflow-y-auto">
                      {languageOptions.map((lang) => (
                        <button
                          key={lang.code}
                          onClick={() => handleLanguageChange(lang.code)}
                          className={`w-full text-left px-3 py-2 flex items-center space-x-2 hover:bg-blue-50 transition duration-200 text-sm ${getCurrentLanguageCode() === lang.code ? 'bg-blue-50 font-medium border-r-2 border-blue-500' : ''}`}
                        >
                          <span className="text-base">{lang.flag}</span>
                          <span className="text-gray-700">{lang.name}</span>
                          {getCurrentLanguageCode() === lang.code && (
                            <Check className="h-3 w-3 text-blue-500 ml-auto" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Compact View Type buttons */}
              <div className="flex items-center bg-gray-100 p-0.5 rounded-lg shadow-inner">
                <button
                  onClick={() => setViewType("grid")}
                  className={`p-2 rounded-md transition-all duration-300 ${viewType === "grid" ? "bg-yellow-500 text-white shadow-md transform scale-105" : "text-gray-600 hover:bg-gray-200"}`}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                </button>
                <button
                  onClick={() => setViewType("list")}
                  className={`p-2 rounded-md transition-all duration-300 ${viewType === "list" ? "bg-yellow-500 text-white shadow-md transform scale-105" : "text-gray-600 hover:bg-gray-200"}`}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Enhanced Search and filters */}
      <div className="max-w-7xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 mb-8">
          {/* Enhanced Search Bar */}
          <div className="relative flex-1 max-w-md">
            <div className={`absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none transition-all duration-300 ${searchFocused ? 'text-yellow-500' : 'text-gray-400'}`}>
              <Search size={20} />
            </div>
            <input
              type="text"
              className={`block w-full pl-12 pr-4 py-4 border-2 rounded-xl leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 text-sm shadow-lg transition-all duration-300 ${searchFocused ? 'shadow-xl border-yellow-300' : 'border-gray-200'}`}
              placeholder={translations[language].SearchProjects}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onFocus={() => setSearchFocused(true)}
              onBlur={() => setSearchFocused(false)}
            />
            {searchTerm && (
              <button
                onClick={() => setSearchTerm("")}
                className="absolute inset-y-0 right-0 pr-4 flex items-center text-gray-400 hover:text-gray-600"
              >
                <X size={16} />
              </button>
            )}
          </div>

          {/* Enhanced Filters */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 bg-white p-2 rounded-xl shadow-lg border border-gray-200">
              <Filter size={18} className="text-gray-500" />
              <select
                className="block pl-2 pr-8 py-2 text-sm border-0 focus:outline-none focus:ring-0 bg-transparent"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                {statuses.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>

            {/* Enhanced Clear Filters Button */}
            {(searchTerm || filterStatus !== "All Status") && (
              <button
                onClick={clearFilters}
                className="flex items-center px-4 py-3 text-sm font-medium text-gray-600 bg-white hover:bg-gray-50 border-2 border-gray-200 hover:border-red-300 rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                title="Clear all filters"
              >
                <X size={16} className="mr-2" />
                {translations[language].ClearAll}
              </button>
            )}
          </div>
        </div>

        {/* Enhanced Display count */}
        <div className="flex justify-between items-center mb-6 p-4 bg-white rounded-xl shadow-sm border border-gray-200">
          <p className="text-sm text-gray-600">
            {translations[language].ShowingOfTotal} <span className="font-bold text-yellow-600">{filteredProjects.length}</span> {translations[language].of} <span className="font-bold">{projects.length}</span> {translations[language].TProjects}
          </p>
          {filteredProjects.length > 0 && (
            <div className="flex items-center text-sm text-gray-500">
              <TrendingUp size={16} className="mr-2 text-green-500" />
              <span>{Math.round((filteredProjects.filter(p => p.isActive).length / filteredProjects.length) * 100)}% {translations[language].Active}</span>
            </div>
          )}
        </div>

        {/* Enhanced Projects grid/list */}
        {filteredProjects.length > 0 ? (
          <>
            {viewType === "grid" ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8">
                {currentProjects.map((project, index) => (
                  <ProjectCard key={project.id} project={project} index={index} />
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200">
                <ul className="divide-y divide-gray-100">
                  {currentProjects.map((project, index) => (
                    <li
                      key={project.id}
                      className="hover:bg-gradient-to-r hover:from-yellow-50 hover:to-orange-50 cursor-pointer transition-all duration-300 group"
                      onClick={() => handleProjectClick(project)}
                      style={{
                        animationDelay: `${index * 50}ms`,
                        animation: `fadeInLeft 0.5s ease-out forwards`
                      }}
                    >
                      <div className="flex items-center px-6 py-6 sm:px-8">
                        <div className="min-w-0 flex-1 flex items-center">
                          <div className="flex-shrink-0">
                            <img
                              className="h-20 w-20 rounded-xl object-cover shadow-lg border-2 border-gray-200 group-hover:border-yellow-300 transition-all duration-300"
                              src={project.image}
                              alt={project.title}
                              onError={(e) => {
                                e.target.src = "/api/placeholder/400/300";
                              }}
                            />
                          </div>
                          <div className="min-w-0 flex-1 px-6">
                            <div>
                              <p className="text-lg font-bold text-gray-900 truncate group-hover:text-yellow-700 transition-colors">{project.title}</p>
                              <div className="mt-2 flex items-center text-sm text-gray-600">
                                <MapPin size={16} className="flex-shrink-0 mr-2 text-yellow-500" />
                                <p className="truncate">{project.location}</p>
                              </div>
                              <div className="mt-3">
                                <div className="w-full bg-gray-200 rounded-full h-2.5 shadow-inner">
                                  <div
                                    className="bg-gradient-to-r from-yellow-400 to-orange-500 h-2.5 rounded-full transition-all duration-500"
                                    style={{ width: `${project.progress}%` }}
                                  ></div>
                                </div>
                                <div className="text-xs text-gray-500 mt-1">{project.progress}% Complete</div>
                              </div>
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-col items-end ml-4 space-y-2">
                          <span className="text-xs px-3 py-1.5 bg-yellow-100 text-yellow-800 rounded-full font-medium border border-yellow-200">
                            {project.status}
                          </span>
                          <span className={`text-xs px-3 py-1.5 rounded-full font-medium border ${project.isActive
                            ? 'bg-green-100 text-green-800 border-green-200'
                            : 'bg-gray-100 text-gray-800 border-gray-200'
                            }`}>
                            {project.isActive ? 'Active' : 'Inactive'}
                          </span>
                          <span className="text-sm text-gray-600 font-medium">{project.area}</span>
                        </div>
                        <div className="ml-6 flex-shrink-0">
                          <ChevronRight size={24} className="text-gray-400 group-hover:text-yellow-500 group-hover:translate-x-1 transition-all duration-300" />
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Enhanced Pagination */}
            {totalPages > 1 && (
              <div className="mt-12 flex justify-center">
                <nav className="relative z-0 inline-flex rounded-xl shadow-lg border border-gray-200 bg-white overflow-hidden" aria-label="Pagination">
                  <button
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className={`relative inline-flex items-center px-4 py-3 text-sm font-medium transition-all duration-200 ${currentPage === 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-700 hover:bg-yellow-50 hover:text-yellow-600'}`}
                  >
                    <ChevronRight size={18} className="transform rotate-180" />
                    <span className="ml-1">{translations[language].Previous}</span>
                  </button>

                  {[...Array(totalPages)].map((_, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrentPage(i + 1)}
                      className={`relative inline-flex items-center px-4 py-3 text-sm font-medium transition-all duration-200 ${currentPage === i + 1
                        ? 'z-10 bg-gradient-to-r from-yellow-500 to-orange-500 text-white shadow-lg'
                        : 'text-gray-700 hover:bg-yellow-50 hover:text-yellow-600'
                        }`}
                    >
                      {i + 1}
                    </button>
                  ))}

                  <button
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className={`relative inline-flex items-center px-4 py-3 text-sm font-medium transition-all duration-200 ${currentPage === totalPages ? 'text-gray-300 cursor-not-allowed' : 'text-gray-700 hover:bg-yellow-50 hover:text-yellow-600'}`}
                  >
                    <span className="mr-1">{translations[language].Next}</span>
                    <ChevronRight size={18} />
                  </button>
                </nav>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-16 bg-white rounded-2xl shadow-xl border border-gray-200">
            <div className="mx-auto h-24 w-24 text-gray-300 mb-6">
              <div className="w-full h-full bg-gray-100 rounded-full flex items-center justify-center">
                <Search size={48} />
              </div>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">{translations[language].NoProjectsFound}</h3>
            <p className="text-gray-500 mb-6">{translations[language].TryAdjusting}</p>
            <button
              onClick={clearFilters}
              className="bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white px-6 py-3 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              {translations[language].ClearAllFilters}
            </button>
          </div>
        )}
      </div>

      {/* Project detail modal */}
      {activeProject && (
        <ProjectDetail
          project={activeProject}
          loading={projectDetailsLoading}
          onClose={() => setActiveProject(null)}
        />
      )}

      {/* Enhanced Login popup modal */}
      {showLoginPopup && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 animate-in fade-in duration-300">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full mx-4 relative border border-gray-200 animate-in slide-in-from-bottom-4 duration-500">
            <button
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100 transition-all duration-200"
              onClick={toggleLoginPopup}
            >
              <X size={20} />
            </button>
            <div className="mt-2">
              <Login
                onLoginSuccess={() => {
                  setShowLoginPopup(false);

                  // Get stored proposal data instead of project data
                  const storedProposalData = sessionStorage.getItem('selectedProposalData');
                  const userData = localStorage.getItem("userData");

                  if (storedProposalData && userData) {
                    const selectedProposal = JSON.parse(storedProposalData);
                    const user = JSON.parse(userData);

                    // Check objection flags from the PROPOSAL (not project)
                    const generalObjAvailable = selectedProposal.general_objection === true;
                    const privateObjAvailable = selectedProposal.private_objection === true;

                    console.log('Proposal objection flags for navigation:', {
                      generalObjAvailable,
                      privateObjAvailable,
                      proposalId: selectedProposal.acquisition_proposal_id,
                      proposalNo: selectedProposal.acquisition_proposal_no,
                      userRole: user.user_role
                    });

                    // Updated navigation logic: if both are true, go to PrivateObjection
                    if (generalObjAvailable && privateObjAvailable) {
                      // Both objections available - navigate to Private Objection
                      console.log('Navigating to Private Objection (both objections are enabled)');
                      navigate('/PrivateObjection');
                    } else if (generalObjAvailable && !privateObjAvailable) {
                      // Only general objection is available
                      console.log('Navigating to General Objection (only general available)');
                      navigate('/GeneralObjection');
                    } else if (!generalObjAvailable && privateObjAvailable) {
                      // Only private objection is available
                      console.log('Navigating to Private Objection (only private available)');
                      navigate('/PrivateObjection');
                    } else {
                      // Neither objection type is available (this shouldn't happen due to earlier validation)
                      console.log('No objections are available for this proposal');
                      alert(`No objections are currently available for proposal ${selectedProposal.acquisition_proposal_no || selectedProposal.acquisition_proposal_id}.`);
                      // Don't navigate anywhere, keep user on current page
                      return;
                    }

                    // Clean up session storage after navigation decision
                    sessionStorage.removeItem('selectedProjectData');
                    sessionStorage.removeItem('selectedProposalData');
                  } else {
                    // If no proposal data, just navigate to dashboard
                    console.warn('No proposal data found in session storage');
                    navigate('/Dashboard');
                  }
                }}
                onClose={() => setShowLoginPopup(false)}
              />
            </div>
          </div>
        </div>
      )}

    </div>
  );
}